import React from 'react';
import { toast } from 'react-toastify';
import Toast from './Toast';
import ToastBody from './ToastBody';
import { logMixpanelEvent } from '@/Utils/eventLogger';

const ShowToast = ({
  type = 'error',
  text = '',
  contentHeader = '',
  onClose = () => {},
  customBody: CustomBody = undefined,
  errorContext,
  endpoint = '',
  payload = {},
  responseStatus = -1,
  fileAndFunction = '',
  other = {},
  autoClose = 5000,
  closeOnClick = false,
  closeButton = false,
  toastId = '',
  noGraphics = false, // If true, no icon will be displayed which might be useful when there is no internet connection
}) => {
  const generalErrorMsg =
    'Something went wrong. Try again or contact support@gigasheet.com if the error persists.';
  const getSafeText = (input) => {
    try {
      if (typeof input === 'string') return input;
      if (input instanceof Error) return input.message;
      return String(input);
    } catch {
      return generalErrorMsg;
    }
  };

  const normalizeText = (input) => {
    try {
      // If it's an Error, use its message
      if (input instanceof Error) {
        return input.message;
      }
      // If it's plain text or a valid React element, pass it through
      if (typeof input === 'string' || React.isValidElement(input)) {
        return input;
      }
      // Fallback for everything else
      return generalErrorMsg;
    } catch {
      return generalErrorMsg;
    }
  };

  const normalizedText = normalizeText(text);
  const safeText = getSafeText(normalizedText);
  const safeContentHeader = getSafeText(contentHeader);
  const safeType = getSafeText(type);

  try {
    if (safeType === 'error') {
      const { message: errMsg = '', Message: altMsg = '', stack = '' } = errorContext || {};
      const formattedErrorDetails = {
        errorMessage: errMsg || altMsg,
        errorStack: stack.substring(0, 500) || 'No stack trace', // Truncate for brevity
        fileAndFunction,
        endpoint,
        payload,
        responseStatus,
        other,
      };

      try {
        logMixpanelEvent('Error Toast Appeared', {
          ToastTextAsString: safeText,
          errorDetails: formattedErrorDetails,
        });
      } catch (logErr) {
        console.error('ShowToast: mixpanel logging failed', logErr);
      }
    }

    const renderToastBody = (closeToast) => {
      try {
        if (CustomBody) {
          return <CustomBody closeToast={closeToast} />;
        }
        return (
          <ToastBody type={safeType} text={normalizedText} contentHeader={safeContentHeader} />
        );
      } catch (bodyErr) {
        console.error('ShowToast: body render error', bodyErr);
        return <ToastBody type={safeType} text={safeText} contentHeader={safeContentHeader} />;
      }
    };

    return toast(
      ({ closeToast }) => (
        <Toast
          closeToast={() => {
            try {
              closeToast();
              onClose();
            } catch (closeErr) {
              console.error('ShowToast: onClose error', closeErr);
            }
          }}
          type={safeType}
          noGraphics={noGraphics}
          children={renderToastBody(closeToast)}
        />
      ),
      {
        autoClose,
        closeButton,
        closeOnClick,
        hideProgressBar: true,
        toastId,
      }
    );
  } catch (fatalErr) {
    console.error('ShowToast: unexpected error', fatalErr);
    return toast.error(generalErrorMsg);
  }
};
export default ShowToast;
